import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, MessageSquare, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { DOWNLOAD_EVENT } from "@/lib/device";
import { RatingBadge } from "@/components/reviews/Stars";
import { ReviewForm } from "@/components/reviews/ReviewForm";
import { ReviewItem } from "@/components/reviews/ReviewItem";
import { ConfirmButton } from "@/components/admin/ConfirmButton";

const PAGE = 10;

// Public reviews block on the app detail page (no owner replies by design).
export const ReviewsSection = ({ appUrl, onRating }) => {
  const { t } = useI18n();
  const url = `${appUrl}/reviews`;
  const [data, setData] = useState(null);
  const [extra, setExtra] = useState([]);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => api.get(url, { params: { limit: PAGE } }).then(({ data: d }) => {
    setData(d);
    setExtra([]);
    onRating?.(d.rating);
  }).catch(() => setData((d) => d || { items: [], total: 0, can_review: false })), [url, onRating]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const again = () => setTimeout(load, 1500);
    window.addEventListener(DOWNLOAD_EVENT, again);
    return () => window.removeEventListener(DOWNLOAD_EVENT, again);
  }, [load]);

  const more = async () => {
    const { data: d } = await api.get(url, { params: { skip: data.items.length + extra.length, limit: PAGE } });
    setExtra((x) => [...x, ...d.items]);
  };
  const removeMine = async () => {
    try {
      await api.delete(`${url}/mine`);
      toast.success(t("reviews.deleted"));
      load();
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };

  if (!data) return <Loader2 className="mt-8 h-5 w-5 animate-spin text-[var(--gold)]" />;
  const items = [...data.items, ...extra].filter((r) => !r.mine);
  const shownCount = data.items.length + extra.length;
  return (
    <section className="mt-10 min-w-0 space-y-4" data-testid="reviews-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-display text-base font-bold uppercase tracking-wider text-white md:text-lg">
          <MessageSquare className="h-5 w-5 text-[var(--gold)]" /> {t("reviews.title")}
        </h2>
        <RatingBadge rating={data.rating} testId="reviews-summary" />
      </div>
      {data.mine && !editing && (
        <ul className="list-none"><ReviewItem review={data.mine} url={url} actions={<>
          <button type="button" className="btn-gold btn-sm" onClick={() => setEditing(true)} data-testid="review-edit-btn"><Pencil className="h-4 w-4" /> {t("reviews.edit")}</button>
          <ConfirmButton title={t("reviews.deleteTitle")} description={t("reviews.deleteDesc")} onConfirm={removeMine} className="btn-red btn-sm" testId="review-delete-mine-btn">
            <Trash2 className="h-4 w-4" /> {t("reviews.delete")}
          </ConfirmButton>
        </>} /></ul>
      )}
      {(editing || (!data.mine && data.can_review)) && (
        <ReviewForm url={url} mine={editing ? data.mine : null} onCancel={editing ? () => setEditing(false) : null}
          onSaved={() => { setEditing(false); load(); }} />
      )}
      {!data.mine && !data.can_review && (
        <p className="flex items-center gap-2 rounded-2xl border border-[rgba(245,197,66,0.25)] bg-black/30 p-4 text-sm text-neutral-300" data-testid="reviews-download-first">
          <Download className="h-4 w-4 shrink-0 text-[var(--gold)]" /> {t("reviews.downloadFirst")}
        </p>
      )}
      {data.total === 0 && <p className="text-sm text-neutral-500" data-testid="reviews-empty">{t("reviews.empty")}</p>}
      <ul className="space-y-3" data-testid="reviews-list">
        {items.map((r) => <ReviewItem key={r.id} review={r} url={url} />)}
      </ul>
      {shownCount < data.total && (
        <button type="button" className="btn-red btn-sm" onClick={more} data-testid="reviews-more-btn">{t("reviews.more")}</button>
      )}
    </section>
  );
};
